import { ref, Ref } from 'vue'
import { replacePlaceholders } from '@/helpers/whatsappPlaceholders'
import { DealDetails } from '@/classes/DealDetails'
import { Article } from '@/classes/Articles'
import { OutletItem } from '@/classes/OutletItem'
import type { ConfigWithExtras } from './useWhatsAppConfigurations'
import { formatWhatsAppMessage, getMessageUrl } from '@/utils/whatsAppMessageFormatter'
import util from '@/helpers/util'

type SelectedContent = DealDetails | Article | OutletItem | null

export function useWhatsAppMessageGeneration(
    openConfigurations: Ref<ConfigWithExtras[]>,
    globalMediaUrl?: Ref<string | null>,
    updateAllMedia?: () => void
) {
    const selectedContentId = ref<number | string | null>(null)
    const selectedContent = ref<SelectedContent>(null)
    const globalTagline = ref('')

    const cleanupMessageTemplate = (message: string, content: SelectedContent): string => {
        if (!message || !content) return message

        // Only clean up pricing-related placeholders for deals
        if (content instanceof DealDetails || ('dealId' in content && typeof (content as any).dealId === 'number')) {
            const deal = content as DealDetails
            const hasRetailPrice = deal.retailPrice !== undefined && deal.retailPrice >= 0

            if (!hasRetailPrice) {
                return message
                    .split('\n')
                    .filter(line => {
                        const trimmedLine = line.trim()
                        return !trimmedLine.includes('{{retailPrice}}') &&
                            !trimmedLine.includes('{{discount}}') &&
                            !trimmedLine.match(/~.*Was.*\$\{\{retailPrice\}\}.*~/i)
                    })
                    .join('\n')
                    .replace(/\n{3,}/g, '\n\n')
                    .trim()
            }
        }

        return message
    }

    const generateMessagesForConfiguration = (index: number) => {
        if (!selectedContent.value) return
        if (index < 0 || index >= openConfigurations.value.length) return

        const config = openConfigurations.value[index]
        if (!config) return

        const tagline = config.taglineOverride || config.defaultTagLine || 'Great Deal!'
        
        // Get template content based on selected template name
        let messageTemplate = config.editedMessage || ''
        if (!messageTemplate && config.selectedTemplateName) {
            const selectedTemplate = config.messageTemplates?.find(t => t.name === config.selectedTemplateName)
            messageTemplate = selectedTemplate?.content || config.defaultMessage || ''
        } else if (!messageTemplate) {
            messageTemplate = config.defaultMessage || ''
        }

        messageTemplate = cleanupMessageTemplate(messageTemplate, selectedContent.value)

        openConfigurations.value[index].editedMessage = messageTemplate

        let generatedMessage = replacePlaceholders(messageTemplate, selectedContent.value, tagline)

        if (generatedMessage.includes('{{uniqueId}}')) {
            const uniqueIdPrefix = config.uniqueIdPrefix || ''
            const uniqueId = uniqueIdPrefix + '1'
            generatedMessage = generatedMessage.replace(/\{\{uniqueId\}\}/g, uniqueId)
        }

        openConfigurations.value[index].generatedMessage = generatedMessage
        openConfigurations.value[index].displayMessage = generatedMessage
    }

    const generateMessagesForAllConfigurations = () => {
        if (!selectedContent.value) return
        if (openConfigurations.value.length === 0) return

        openConfigurations.value.forEach((config, index) => {
            generateMessagesForConfiguration(index)
        })
    }

    const onContentSelected = (content: DealDetails | Article | OutletItem) => {
        if (!content) {
            console.error('Invalid content selected:', content)
            return
        }

        selectedContent.value = content

        // Extract ID based on content type
        if (content instanceof DealDetails || ('dealId' in content && typeof (content as any).dealId === 'number')) {
            selectedContentId.value = (content as DealDetails).dealId
        } else if (content instanceof Article || ('articleId' in content && typeof (content as any).articleId === 'number')) {
            selectedContentId.value = (content as Article).articleId
        } else if (content instanceof OutletItem || ('id' in content && typeof (content as any).id === 'string')) {
            selectedContentId.value = (content as OutletItem).id
        }

        generateMessagesForAllConfigurations()
    }

    // Keep onDealSelected for backward compatibility
    const onDealSelected = (deal: DealDetails) => {
        onContentSelected(deal)
    }

    const updateConfigTagline = (index: number, tagline: string) => {
        openConfigurations.value[index].taglineOverride = tagline
        if (selectedContent.value) {
            generateMessagesForConfiguration(index)
        }
    }

    const updateAllTaglines = (value?: string) => {
        const taglineToApply = value !== undefined ? value : globalTagline.value
        if (!taglineToApply) return
        if (!selectedContent.value) return

        openConfigurations.value.forEach((config, index) => {
            config.editedMessage = config.defaultMessage || ''
            config.taglineOverride = taglineToApply
            generateMessagesForConfiguration(index)
        })

        applyAllChanges()
    }

    const updateConfigMessage = (index: number, message: string) => {
        const config = openConfigurations.value[index]
        const hasPlaceholders = /\{\{[^}]+\}\}/.test(message)

        if (hasPlaceholders && selectedContent.value) {
            let cleanedMessage = message
            cleanedMessage = cleanupMessageTemplate(message, selectedContent.value)
            config.editedMessage = cleanedMessage

            const tagline = config.taglineOverride || config.defaultTagLine || 'Great Deal!'
            let messageTemplate = cleanedMessage
            if (!messageTemplate) {
                const selectedTemplate = config.messageTemplates?.find(t => t.name === config.selectedTemplateName)
                messageTemplate = selectedTemplate?.content || config.defaultMessage || ''
            }
            let displayMessage = replacePlaceholders(messageTemplate, selectedContent.value, tagline)

            if (displayMessage.includes('{{uniqueId}}')) {
                const uniqueIdPrefix = config.uniqueIdPrefix || ''
                const uniqueId = uniqueIdPrefix + '1'
                displayMessage = displayMessage.replace(/\{\{uniqueId\}\}/g, uniqueId)
            }

            config.displayMessage = displayMessage
            config.generatedMessage = displayMessage
        } else {
            config.displayMessage = message
            config.generatedMessage = message
            config.editedMessage = message
        }
    }

    const applyAllChanges = () => {
        if (!selectedContent.value) {
            util.showToast('Please select content first', { icon: 'error', iconClass: 'toast-icon-circle' })
            return
        }

        openConfigurations.value.forEach((config, index) => {
            // Get template content based on selected template name
            let defaultTemplate = config.defaultMessage || ''
            if (config.selectedTemplateName && config.messageTemplates) {
                const selectedTemplate = config.messageTemplates.find(t => t.name === config.selectedTemplateName)
                defaultTemplate = selectedTemplate?.content || config.defaultMessage || ''
            }
            config.editedMessage = defaultTemplate

            if (globalTagline.value) {
                config.taglineOverride = globalTagline.value
            }

            generateMessagesForConfiguration(index)
        })

        if (globalMediaUrl?.value && updateAllMedia) {
            updateAllMedia()
        }

        const changes: string[] = []
        if (globalTagline.value) changes.push('tagline')
        if (globalMediaUrl?.value) changes.push('media')

    }

    const restoreDefaultMessage = (index: number) => {
        const config = openConfigurations.value[index]
        
        // Get template content based on selected template name
        let templateContent = config.defaultMessage
        if (config.selectedTemplateName && config.messageTemplates) {
            const selectedTemplate = config.messageTemplates.find(t => t.name === config.selectedTemplateName)
            templateContent = selectedTemplate?.content || config.defaultMessage
        }
        
        let restoredMessage = templateContent

        if (selectedContent.value) {
            restoredMessage = cleanupMessageTemplate(templateContent, selectedContent.value)
        }

        config.editedMessage = restoredMessage
        if (selectedContent.value) {
            generateMessagesForConfiguration(index)
        } else {
            config.displayMessage = restoredMessage
        }
    }

    return {
        selectedContentId,
        selectedContent,
        selectedDealId: selectedContentId, // Backward compatibility
        selectedDeal: selectedContent, // Backward compatibility
        globalTagline,
        onContentSelected,
        onDealSelected, // Backward compatibility
        generateMessagesForAllConfigurations,
        generateMessagesForConfiguration,
        updateConfigTagline,
        updateAllTaglines,
        updateConfigMessage,
        applyAllChanges,
        restoreDefaultMessage,
        formatWhatsAppMessage,
        getMessageUrl
    }
}

